import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { useInfiniteScroll } from '@/shared/composables/useInfiniteScroll'
import { ScrollTopButton } from '@/shared/ui/ScrollTopButton'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { EmptyStatePage } from '@/shared/ui/EmptyState'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import type { Paged, ProblemCard } from '@/shared/lib/apiTypes'
import { ProblemIssueCard, ProblemListCard } from '@/shared/ui/IssueCard'
import { IssueIcon } from '@/public/features/home/ui/HomeVariants'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { formatDate } from '@/shared/lib/format'
import { useProblemListFilter } from '@/public/features/problem/composables/problemListFilter'
import { FilterModalPc } from '@/shared/ui/FilterModalPc'
import { CategoryTabBar } from '@/shared/ui/CategoryTabBar'
import { useRowCarousel } from '@/shared/composables/useRowCarousel'
import { CarouselArrows } from '@/shared/ui/CarouselArrows'
import { CARD_CATEGORY_MAX } from '@/shared/constants/cards'
import { RECENT_COUNT } from '@/public/features/problem/constants/list'

export function ProblemListPc({ query }: { query: UseInfiniteQueryResult<InfiniteData<Paged<ProblemCard>>> }) {
  const { categoryLabel, isReady } = useCardLabels()
  const f = useProblemListFilter(query)
  const endRef = useInfiniteScroll(
    () => void query.fetchNextPage(),
    query.hasNextPage && !query.isFetchingNextPage,
  )

  const recent = useRowCarousel()

  return (
    <DefaultLayout tree="pc" className="[--body-w:1000px]">
      <QueryState
        isPending={query.isPending || !isReady}
        error={query.error}
        isEmpty={f.all.length === 0}
        empty={
          <EmptyStatePage
            illust="/illust/empty-problem-large.png"
            title="아직 등록된 시민 문제가 없어요."
            lines={['일상에서 겪는 불편함이나', '해결이 필요한 문제를 알려주세요.']}
            actionText="우리 동네 문제 등록하기"
            actionTo="/problems/new"
          />
        }
      >
        <div className="layout-body pt-8 pb-[30px]">
          <section>
            <h2 className="m-0 flex h-8 items-center text-body-lg font-semibold text-black-600">
              최근 등록된 시민 문제
            </h2>
            <div className="relative mt-[18px]">
              <ul
                ref={recent.ref}
                className="m-0 flex list-none items-stretch gap-4 overflow-x-auto p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {f.all.slice(0, RECENT_COUNT).map((p, i) => {
                  const count = f.counts.get(p.id)
                  return (
                    <li key={p.id} className="flex w-[322.67px] min-w-0 shrink-0">
                      <Link
                        className="flex w-full min-w-0 text-inherit no-underline"
                        to={`/problems/${p.id}`}
                      >
                        <ProblemIssueCard
                          categories={(p.categoryCodes ?? [])
                            .slice(0, CARD_CATEGORY_MAX)
                            .map(categoryLabel)}
                          title={p.title}
                          summary={p.summary ?? undefined}
                          icon={<IssueIcon index={i} />}
                          metaItems={[formatDate(p.createdAt), p.region.name]}
                          {...(count !== undefined ? { actionText: `적합카드 ${count}장`, actionMuted: count === 0 } : {})}
                        />
                      </Link>
                    </li>
                  )
                })}
              </ul>
              <CarouselArrows control={recent} label="최근 등록된 시민 문제" top={100} />
            </div>
          </section>

          <section className="pt-[42px]">
            <CategoryTabBar
              groups={f.groups.data ?? []}
              selected={f.group}
              onSelect={f.selectGroup}
              hasFilter={f.hasFilter}
              filterOpen={f.sheetOpen}
              onOpenFilter={() => f.setSheetOpen(true)}
            />

            <div className="mt-[22px] grid grid-cols-3 items-stretch gap-4">
              {f.items.map((p) => {
                const count = f.counts.get(p.id)
                return (
                  <Link key={p.id} className="flex text-inherit no-underline" to={`/problems/${p.id}`}>
                    <ProblemListCard
                      categories={(p.categoryCodes ?? [])
                        .slice(0, CARD_CATEGORY_MAX)
                        .map(categoryLabel)}
                      title={p.title}
                      summary={p.summary ?? undefined}
                      metaItems={[formatDate(p.createdAt), p.region.name]}
                      {...(count !== undefined ? { actionText: `적합카드 ${count}장`, actionMuted: count === 0 } : {})}
                    />
                  </Link>
                )
              })}
            </div>
          </section>
        </div>

      <div ref={endRef} aria-hidden="true" />
      {query.isFetchingNextPage && (
        <p className="m-0 py-4 text-center text-body-sm text-black-100">더 불러오는 중…</p>
      )}
      </QueryState>

      {f.sheetOpen && (
        <FilterModalPc
          label="시민문제 필터"
          value={f.filter}
          categories={f.categories.data ?? []}
          lifecycle={f.lifecycle.data ?? []}
          situation={f.situation.data ?? []}
          dongs={f.dongs.data ?? []}
          onApply={f.applyFilter}
          onClose={() => f.setSheetOpen(false)}
        />
      )}

      <ScrollTopButton />
    </DefaultLayout>
  )
}

